import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Paperclip, Upload, Trash2, FileText, Loader2, ExternalLink, Lock, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fileUrl, downloadUrl, uploadAttachment, deleteAttachment } from "@/lib/api";

const isImage = (a) => (a.content_type || "").startsWith("image/") || a.kind === "photo";

export default function AttachmentsCell({ row, path, label = "Files" }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [initials, setInitials] = useState("");
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);

  const atts = row.attachments || [];
  const locked = !!row.archived; // archived North Shop records are read-only for evidence integrity

  const refresh = () => qc.invalidateQueries({ queryKey: [path] });

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (!initials.trim()) { toast.error("Enter your initials before uploading"); return; }
    setBusy(true);
    let ok = 0;
    try {
      for (const file of files) {
        const kind = (file.type || "").startsWith("image/") ? "photo" : "document";
        await uploadAttachment(path, row.id, file, kind, initials.trim());
        ok += 1;
      }
      refresh();
      toast.success(ok > 1 ? `${ok} files attached` : "File attached");
    } catch (err) {
      refresh();
      toast.error(err?.response?.data?.detail || `Uploaded ${ok}/${files.length} — an upload failed`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (attId) => {
    setDelId(attId);
    try {
      await deleteAttachment(path, row.id, attId);
      refresh();
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    } finally {
      setDelId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`${path}-attachments-btn`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 text-xs font-bold uppercase tracking-wide transition-colors ${
            atts.length ? "border-[#16265F] text-[#16265F] hover:bg-[#16265F] hover:text-white" : "border-gray-300 text-gray-400 hover:border-gray-900 hover:text-gray-900"
          }`}
        >
          <Paperclip className="w-3.5 h-3.5" />
          {atts.length || "Add"}
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-2 border-gray-900 max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="shrink-0 p-6 pb-2">
          <DialogTitle className="font-head text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
            {label} {locked && <Lock className="w-5 h-5 text-[#E0A81E]" />}
          </DialogTitle>
          <DialogDescription>
            {locked ? "This record is archived — files are locked for the record." : "Attach photos and documents to this unit."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-2 space-y-3" data-testid={`${path}-attachments-list`}>
          {atts.length === 0 && <div className="text-sm text-gray-400 py-6 text-center">No files yet.</div>}
          {atts.map((a) => (
            <div key={a.id} className="flex items-center gap-3 border-2 border-gray-200 p-2">
              {isImage(a) ? (
                <a href={fileUrl(a.path)} target="_blank" rel="noreferrer" className="shrink-0">
                  <img src={fileUrl(a.path)} alt={a.filename} className="w-14 h-14 object-cover border border-gray-300" />
                </a>
              ) : (
                <div className="w-14 h-14 flex items-center justify-center bg-gray-100 border border-gray-300 shrink-0">
                  <FileText className="w-6 h-6 text-gray-500" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <a href={fileUrl(a.path)} target="_blank" rel="noreferrer"
                  className="font-medium text-sm text-[#16265F] hover:underline flex items-center gap-1 truncate">
                  <span className="truncate">{a.filename}</span><ExternalLink className="w-3 h-3 shrink-0" />
                </a>
                <div className="text-xs text-gray-500 uppercase font-mono-data">{a.initials || "—"}</div>
              </div>
              <a href={downloadUrl(a.path, a.filename)} data-testid={`${path}-attachment-download`}
                className="shrink-0 text-[#16265F] hover:bg-blue-50 p-2 rounded-none" title="Download">
                <Download className="w-4 h-4" />
              </a>
              {!locked && (
                <button data-testid={`${path}-attachment-delete`} onClick={() => onDelete(a.id)} disabled={delId === a.id}
                  className="shrink-0 text-[#D50000] hover:bg-red-100 p-2 rounded-none">
                  {delId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              )}
            </div>
          ))}
        </div>

        {!locked && (
          <div className="shrink-0 p-6 pt-4 border-t border-gray-100 bg-white space-y-3">
            <div className="flex items-end gap-3">
              <div className="w-24">
                <label className="text-xs uppercase tracking-widest font-bold text-gray-500">Initials</label>
                <Input data-testid={`${path}-attachment-initials`} value={initials} maxLength={2}
                  onChange={(e) => setInitials(e.target.value.toUpperCase())} placeholder="BW"
                  className="mt-1 rounded-none border-2 uppercase" />
              </div>
              <input ref={fileRef} type="file" multiple className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={onPick}
                data-testid={`${path}-attachment-input`} />
              <Button data-testid={`${path}-attachment-upload-btn`} onClick={() => fileRef.current?.click()} disabled={busy}
                className="rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase flex-1">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload Files
              </Button>
            </div>
            <p className="text-xs text-gray-400">Select multiple photos or documents at once (PDF, Word, Excel).</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
